import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import styles from "../styles/pages/help.module.css";

const Help = () => {
    const canonicalUrl = "https://myonlineshopping.co.in/help";

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Help & Support - Mohit's Shopping App",
        description:
            "Learn how to buy products through Mohit's Shopping App, request an Amazon affiliate link, or ask us to help you purchase a product.",
        url: canonicalUrl,
        isPartOf: {
            "@type": "WebSite",
            name: "Mohit's Shopping App",
            url: "https://myonlineshopping.co.in/",
        },
    };

    return (
        <>
            <Helmet>
                <title>Help & Support | Mohit's Shopping App</title>

                <meta
                    name="description"
                    content="Learn how to buy products through Mohit's Shopping App. Find out how to purchase available products, request an Amazon affiliate link, or ask us to help you buy a product."
                />

                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />

                <meta name="author" content="Mohit Singh" />

                <link rel="canonical" href={canonicalUrl} />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:title"
                    content="Help & Support | Mohit's Shopping App"
                />

                <meta
                    property="og:description"
                    content="Simple step-by-step help for buying products through Mohit's Shopping App and requesting Amazon affiliate links."
                />

                <meta
                    property="og:url"
                    content={canonicalUrl}
                />

                <meta
                    property="og:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Help & Support | Mohit's Shopping App"
                />

                <meta
                    name="twitter:description"
                    content="Learn how to buy products or request an Amazon affiliate link through Mohit's Shopping App."
                />

                <meta
                    name="twitter:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <main className={styles.helpPage}>
                {/* Hero */}
                <section className={styles.hero}>
                    <Container>
                        <div className={styles.heroContent}>
                            <span className={styles.badge}>
                                💡 Help & Support
                            </span>

                            <h1>
                                How Can We Help You?
                            </h1>

                            <p>
                                Want to buy a product through our affiliate
                                program? Follow these simple steps. We make it
                                easy to get the product you want.
                            </p>

                            <span className={styles.updated}>
                                Simple • Easy • Step-by-Step
                            </span>
                        </div>
                    </Container>
                </section>

                <Container className={styles.container}>
                    {/* Quick Answer */}
                    <section className={styles.card}>
                        <div className={styles.intro}>
                            <div className={styles.introIcon}>
                                🛍️
                            </div>

                            <div>
                                <h2>
                                    Want to buy a product?
                                </h2>

                                <p>
                                    There are two easy ways to get started.
                                    If the product is already available on our
                                    website, you can go directly to Amazon.
                                    If you cannot find the product here, you
                                    can send us the product link and we can
                                    help you with the next step.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Option 1 */}
                    <section className={styles.processCard}>
                        <div className={styles.processHeader}>
                            <span className={styles.number}>
                                01
                            </span>

                            <div>
                                <span className={styles.eyebrow}>
                                    PRODUCT ALREADY AVAILABLE
                                </span>

                                <h2>
                                    The product is already on our website
                                </h2>
                            </div>
                        </div>

                        <p className={styles.processDescription}>
                            If you find the product you want on Mohit's
                            Shopping App, you do not need to contact us.
                            You can directly visit Amazon and complete your
                            purchase.
                        </p>

                        <div className={styles.steps}>
                            <div className={styles.step}>
                                <span>1</span>
                                <div>
                                    <h3>Find your product</h3>
                                    <p>
                                        Browse our website and open the
                                        product you want to buy.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <span>2</span>
                                <div>
                                    <h3>Click the Amazon button</h3>
                                    <p>
                                        Click the available Amazon purchase
                                        link or button on the product page.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <span>3</span>
                                <div>
                                    <h3>Complete your purchase on Amazon</h3>
                                    <p>
                                        You will be redirected to Amazon.
                                        Review the product and complete your
                                        order there.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.infoBox} ${styles.blueBox}`}>
                            <span>ℹ️</span>

                            <p>
                                Your payment, order, shipping, return and
                                refund are handled by Amazon according to
                                Amazon's applicable policies.
                            </p>
                        </div>
                    </section>

                    {/* Option 2 */}
                    <section className={styles.processCard}>
                        <div className={styles.processHeader}>
                            <span className={`${styles.number} ${styles.greenNumber}`}>
                                02
                            </span>

                            <div>
                                <span className={styles.eyebrow}>
                                    PRODUCT NOT AVAILABLE
                                </span>

                                <h2>
                                    Can't find the product on our website?
                                </h2>
                            </div>
                        </div>

                        <p className={styles.processDescription}>
                            No problem. If the product is not available on
                            our website, you can send us the product URL
                            through our Contact Us page.
                        </p>

                        <div className={styles.steps}>
                            <div className={styles.step}>
                                <span>1</span>
                                <div>
                                    <h3>Find the product on Amazon</h3>
                                    <p>
                                        Open Amazon and find the product you
                                        want to buy.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <span>2</span>
                                <div>
                                    <h3>Copy the product URL</h3>
                                    <p>
                                        Copy the Amazon product link from
                                        your browser or Amazon app.
                                    </p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <span>3</span>
                                <div>
                                    <h3>Open our Contact Us page</h3>
                                    <p>
                                        Go to our Contact Us page and enter
                                        your name, email and product URL.
                                    </p>

                                    <a
                                        href="/contact-us"
                                        className={styles.contactLink}
                                    >
                                        Go to Contact Us →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Message Instructions */}
                    <section className={styles.requestCard}>
                        <div className={styles.requestIcon}>
                            ✉️
                        </div>

                        <div className={styles.requestContent}>
                            <span className={styles.eyebrow}>
                                WHAT SHOULD YOU WRITE?
                            </span>

                            <h2>
                                Tell us what you need
                            </h2>

                            <p>
                                In the message box on the Contact Us page,
                                simply tell us what you want. You can use
                                either of the following options.
                            </p>

                            <div className={styles.messageOptions}>
                                <div className={styles.messageOption}>
                                    <span className={styles.optionIcon}>
                                        🛒
                                    </span>

                                    <div>
                                        <h3>
                                            Want us to help you buy it?
                                        </h3>

                                        <p>
                                            Write something like:
                                        </p>

                                        <div className={styles.example}>
                                            "I want to buy this product.
                                            Please help me purchase it."
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.messageOption}>
                                    <span className={styles.optionIcon}>
                                        🔗
                                    </span>

                                    <div>
                                        <h3>
                                            Want an affiliate link?
                                        </h3>

                                        <p>
                                            Write something like:
                                        </p>

                                        <div className={styles.example}>
                                            "Please generate an affiliate
                                            link for this product and send
                                            it to my email."
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Email / WhatsApp */}
                    <section className={styles.deliveryCard}>
                        <div className={styles.deliveryHeader}>
                            <span className={styles.eyebrow}>
                                HOW WILL YOU RECEIVE THE LINK?
                            </span>

                            <h2>
                                Choose Email or WhatsApp
                            </h2>

                            <p>
                                If you want us to generate an affiliate link,
                                tell us where you would like to receive it.
                            </p>
                        </div>

                        <div className={styles.deliveryGrid}>
                            <div className={styles.deliveryItem}>
                                <div className={styles.deliveryIcon}>
                                    📧
                                </div>

                                <h3>
                                    Email
                                </h3>

                                <p>
                                    Enter your email address in the Contact
                                    Us form. We can send the generated
                                    affiliate link back to your email.
                                </p>
                            </div>

                            <div className={styles.deliveryItem}>
                                <div className={`${styles.deliveryIcon} ${styles.whatsappIcon}`}>
                                    💬
                                </div>

                                <h3>
                                    WhatsApp
                                </h3>

                                <p>
                                    If you prefer WhatsApp, include your
                                    WhatsApp number in your message and let us
                                    know that you want the link on WhatsApp.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Important Note */}
                    <section className={styles.notice}>
                        <div className={styles.noticeIcon}>
                            ⚠️
                        </div>

                        <div>
                            <h2>
                                Important
                            </h2>

                            <p>
                                Mohit's Shopping App helps you discover
                                products and provides affiliate links. When
                                you purchase a product through Amazon, the
                                actual order, payment, shipping, delivery,
                                returns and refunds are handled by Amazon
                                and/or the applicable seller.
                            </p>
                        </div>
                    </section>

                    {/* Final CTA */}
                    <section className={styles.cta}>
                        <div>
                            <span className={styles.ctaBadge}>
                                Need a product?
                            </span>

                            <h2>
                                Can't find what you're looking for?
                            </h2>

                            <p>
                                Send us the product URL and tell us what you
                                need. We'll guide you through the process.
                            </p>
                        </div>

                        <a
                            href="/contact-us"
                            className={styles.ctaButton}
                        >
                            Contact Us
                            <span>→</span>
                        </a>
                    </section>

                    {/* Support */}
                    <section className={styles.support}>
                        <p>
                            Still have a question?
                        </p>

                        <div className={styles.supportLinks}>
                            <a href="mailto:mahi8191singh@gmail.com">
                                ✉️ mahi8191singh@gmail.com
                            </a>

                            <a href="tel:+918191042065">
                                📞 +91 8191042065
                            </a>
                        </div>
                    </section>
                </Container>
            </main>
        </>
    );
};

export default Help;
